import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Zap, Trash2, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { AutomationRunHistory } from "@/components/automations/AutomationRunHistory";
import { AutomationTester } from "@/components/automations/AutomationTester";
import { NaturalLanguageAutomationInput } from "@/components/ai/NaturalLanguageAutomationInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface TriggerNode {
  id: string;
  type: "record_created" | "record_updated" | "schedule" | "webhook";
  config: any;
}

interface ActionNode {
  id: string;
  type: "send_email" | "send_whatsapp" | "http_request" | "update_record";
  config: any;
}

const AutomationBuilder = () => {
  const { automationId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [automation, setAutomation] = useState<any>(null);
  const [trigger, setTrigger] = useState<TriggerNode | null>(null);
  const [actions, setActions] = useState<ActionNode[]>([]);
  const [mode, setMode] = useState<"natural" | "manual">("natural");

  useEffect(() => {
    if (automationId) {
      fetchAutomation();
    }
  }, [automationId]);

  const fetchAutomation = async () => {
    try {
      const { data, error } = await supabase
        .from("automations")
        .select("*")
        .eq("id", automationId)
        .single();

      if (error) throw error;
      setAutomation(data);
      
      if (data.trigger_config) {
        setTrigger(data.trigger_config as unknown as TriggerNode);
      }
      if (data.action_config && Array.isArray(data.action_config)) {
        setActions(data.action_config as unknown as ActionNode[]);
      }
    } catch (error: any) {
      toast.error("Failed to load automation");
    } finally {
      setLoading(false);
    }
  };

  const updateAutomation = async () => {
    try {
      const { error } = await supabase
        .from("automations")
        .update({
          name: automation.name,
          description: automation.description,
          status: automation.status,
          trigger_config: trigger as any,
          action_config: actions as any,
        })
        .eq("id", automationId);

      if (error) throw error;
      toast.success("Automation updated");
    } catch (error: any) {
      toast.error("Failed to update automation");
    }
  };

  const addAction = () => {
    setActions([
      ...actions,
      {
        id: Math.random().toString(),
        type: "send_email",
        config: {},
      },
    ]);
  };

  const removeAction = (id: string) => {
    setActions(actions.filter((a) => a.id !== id));
  };

  const updateTriggerConfig = (key: string, value: any) => {
    setTrigger({
      ...trigger!,
      config: { ...trigger!.config, [key]: value },
    });
  };

  const updateActionConfig = (id: string, key: string, value: any) => {
    setActions(
      actions.map((action) =>
        action.id === id
          ? {
              ...action,
              config: { ...action.config, [key]: value },
            }
          : action
      )
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Automation not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="font-bold text-xl">Automation Builder</h1>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={automation.status === "active"}
                onCheckedChange={(checked) =>
                  setAutomation({
                    ...automation,
                    status: checked ? "active" : "paused",
                  })
                }
              />
              <span className="text-sm">
                {automation.status === "active" ? "Active" : "Paused"}
              </span>
              <Button onClick={updateAutomation}>Save</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Configuration */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={automation.name}
                    onChange={(e) =>
                      setAutomation({ ...automation, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={automation.description || ""}
                    onChange={(e) =>
                      setAutomation({ ...automation, description: e.target.value })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Tabs value={mode} onValueChange={(v) => setMode(v as "natural" | "manual")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="natural">Natural Language</TabsTrigger>
                <TabsTrigger value="manual">Manual Config</TabsTrigger>
              </TabsList>

              <TabsContent value="natural" className="mt-4">
                <NaturalLanguageAutomationInput
                  onAutomationParsed={(parsed) => {
                    setAutomation({ ...automation, name: parsed.name });
                    setTrigger({
                      id: Math.random().toString(),
                      type: parsed.trigger.type as any,
                      config: parsed.trigger.config
                    });
                    setActions(parsed.actions.map(action => ({
                      id: Math.random().toString(),
                      type: action.type as any,
                      config: action.config
                    })));
                    toast.success("Automation configured! Review and save.");
                    setMode("manual");
                  }}
                />
              </TabsContent>

              <TabsContent value="manual" className="space-y-6 mt-4">

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Trigger
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!trigger ? (
                  <div className="text-center py-8">
                    <Button
                      onClick={() =>
                        setTrigger({
                          id: "1",
                          type: "record_created",
                          config: {},
                        })
                      }
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Trigger
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Trigger Type</Label>
                      <Select
                        value={trigger.type}
                        onValueChange={(value: any) =>
                          setTrigger({ ...trigger, type: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="record_created">Record Created</SelectItem>
                          <SelectItem value="record_updated">Record Updated</SelectItem>
                          <SelectItem value="schedule">Schedule</SelectItem>
                          <SelectItem value="webhook">Webhook</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {trigger.type === "schedule" && (
                      <div>
                        <Label>Cron Expression</Label>
                        <Input
                          placeholder="0 9 * * *"
                          value={trigger.config.cron || ""}
                          onChange={(e) =>
                            updateTriggerConfig("cron", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {actions.map((action, index) => (
                  <div key={action.id} className="border border-border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Action {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAction(action.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <Label>Action Type</Label>
                      <Select
                        value={action.type}
                        onValueChange={(value: any) =>
                          setActions(
                            actions.map((a) =>
                              a.id === action.id ? { ...a, type: value } : a
                            )
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="send_email">Send Email</SelectItem>
                          <SelectItem value="send_whatsapp">Send WhatsApp</SelectItem>
                          <SelectItem value="http_request">HTTP Request</SelectItem>
                          <SelectItem value="update_record">Update Record</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {action.type === "send_email" && (
                      <>
                        <div>
                          <Label>To</Label>
                          <Input
                            placeholder="email@example.com"
                            value={action.config.to || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "to", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Subject</Label>
                          <Input
                            placeholder="Email subject"
                            value={action.config.subject || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "subject", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Body</Label>
                          <Textarea
                            placeholder="Email body"
                            value={action.config.body || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "body", e.target.value)
                            }
                          />
                        </div>
                      </>
                    )}
                    {action.type === "send_whatsapp" && (
                      <>
                        <div>
                          <Label>Phone Number</Label>
                          <Input
                            placeholder="+1234567890"
                            value={action.config.phone || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "phone", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Message</Label>
                          <Textarea
                            placeholder="WhatsApp message"
                            value={action.config.message || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "message", e.target.value)
                            }
                          />
                        </div>
                      </>
                    )}
                    {action.type === "http_request" && (
                      <>
                        <div>
                          <Label>URL</Label>
                          <Input
                            placeholder="https://api.example.com/endpoint"
                            value={action.config.url || ""}
                            onChange={(e) =>
                              updateActionConfig(action.id, "url", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Method</Label>
                          <Select
                            value={action.config.method || "POST"}
                            onValueChange={(value) =>
                              updateActionConfig(action.id, "method", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GET">GET</SelectItem>
                              <SelectItem value="POST">POST</SelectItem>
                              <SelectItem value="PUT">PUT</SelectItem>
                              <SelectItem value="DELETE">DELETE</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                <Button onClick={addAction} variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Action
                </Button>
              </CardContent>
            </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Testing & History */}
          <div className="space-y-6">
            <AutomationTester automationId={automationId!} />
            <AutomationRunHistory automationId={automationId!} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationBuilder;
